import axios from 'axios';
import * as utils from './Utils';
import * as bcUtils from "common/bigcommerce-utils";
import * as cookieUtils from 'common/cookie-utils';

//====================================
// Identify
//====================================
function identify(data) {
  const cart = data.cart;
  const customer = data.customer;

  const contactData = {};
  if (customer) {
    if (customer.email) contactData["email"] =  customer.email;
    if (customer.name) contactData["name"] =  customer.name;
    if (customer.phone) contactData["phone"] =  customer.phone;
  }

  if (utils.isEmpty(contactData)) return;
  window.OnVoard('web_tracking', 'identify', contactData);
}


//====================================
// Checkout Started
//====================================
function getWebTrackingToken() {
  const el = document.querySelector(".ov-app-web-tracking");
  if (!el) return;
  return el.getAttribute('data-token');
}

function trackCheckoutStarted(data) {
  const pageType = data.page_type;
  const customer = data.customer;
  const isGuest = customer && customer.email ? false : true;

  const eligiblePageTypes = [
    "checkout",
  ];

  if (!eligiblePageTypes.includes(pageType)) return;

  const extractCartProduct = (item, currencyCode) => {
    const productData = {};
    productData['id'] = item.productId;
    productData['name'] = item.name;
    productData['sku'] = item.sku;
    productData['url'] = item.url;
    productData['variant_id'] = item.variantId;
    productData['image_url'] = utils.isValidUrl(item.imageUrl) ? item.imageUrl : null;
    productData['quantity'] = item.quantity;

    const salePrice = utils.formatAmount(bcUtils.convertDefaultRate(item.salePrice, currencyCode));
    productData['price'] = salePrice;
    return productData;
  };

  const handleCheckoutData = (checkoutData) => {
    if (!checkoutData || !checkoutData.grandTotal) return;

    const cartData = checkoutData.cart;
    if (!cartData || !cartData.id) return;

    const currencyCode = cartData.currency.code;
    var recordData = {};
    recordData["cart_id"] = cartData.id;
    recordData["currency"] = window.OnVoardData.currency_selector.default_currency_code;
    recordData["discount_amount"] = bcUtils.convertDefaultRate(cartData.discountAmount, currencyCode);
    recordData["subtotal"] = bcUtils.convertDefaultRate(checkoutData.subtotal, currencyCode);
    recordData["grand_total"] = bcUtils.convertDefaultRate(checkoutData.grandTotal, currencyCode);
    recordData["cart_created_time"] = cartData.createdTime;

    const products = []
    cartData.lineItems.physicalItems.forEach((item) => products.push(extractCartProduct(item, currencyCode)));
    cartData.lineItems.digitalItems.forEach((item) => products.push(extractCartProduct(item, currencyCode)));
    if (products.length === 0) return;
    recordData["products"] = products;

    const webTrackingId = getWebTrackingToken();
    if (!webTrackingId) {
      console.error("Missing web tracking token");
      return;
    }

    const payload = {};
    payload['web_tracking_id'] = webTrackingId;
    payload['email'] = isGuest ? cartData.email : customer.email;
    payload['activity'] = 'bigcommerce.checkout_started';
    payload['record_data'] = recordData;

    // Email/SMS click token, when this visitor arrived through one. Absent means absent -
    // the backend treats a missing ov_click as no click and stores no sentinel for it.
    const ovClick = cookieUtils.getClickId();
    if (ovClick) payload['ov_click'] = ovClick;

    if (customer && customer.name) payload['name'] = customer.name;
    if (customer && customer.phone) payload['phone'] = customer.phone;

    const url = utils.buildUrl(`track-checkout-started`);
    navigator.sendBeacon(url, JSON.stringify(payload));
  };

  const fetchAndTrackCheckout = () => {
    axios.get('/api/storefront/carts?include=lineItems.physicalItems.options,lineItems.digitalItems.options')
      .then(function (response) {
        return response.data;
      }).then(function (cartsData) {
        return cartsData[0].id;
      })
      .catch(function (error) {
        console.log(error);
      }).then(function (cartId) {
        axios.get('/api/storefront/checkouts/' + cartId)
        .then(function (response) {
            return response.data;
        }).then(function (checkoutData) {
            handleCheckoutData(checkoutData);
        }).catch(function (error) {
            console.log(error);
        })
      });
  };

  if (isGuest && !window.OnVoardAddedScripts.bigCommerceTrackCheckoutStarted) {
    window.OnVoardAddedScripts.bigCommerceTrackCheckoutStarted = true;
    document.addEventListener('click', (evt) => {
      var target = evt.target;
      if (!target) return;

      var elId = target.getAttribute("id");
      var triggerIds = [
        "checkout-customer-continue",
        "checkout-shipping-continue",
        "checkout-billing-continue",
      ];

      if (triggerIds.includes(elId)) {
        fetchAndTrackCheckout();
        return;
      }

      // convert to lower case and only keep a-z characters
      var elClass = target.getAttribute("class") || '';
      elClass = elClass.toLowerCase().replace(/[^a-z]/gi, '');

      if (elClass && elClass.includes("customeremail")) {
        fetchAndTrackCheckout();
        return;
      }
    });
  }

  if (!isGuest) {
    fetchAndTrackCheckout();
  }
}



//====================================
// Product Viewed
//====================================
// Prices shown on product page is based on shop's default currency
function trackProductViewed(data) {
  const pageType = data.page_type;
  const shop = data.shop;
  const product = data.product;

  const eligiblePageTypes = [
    "product",
  ];

  if (!eligiblePageTypes.includes(pageType)) return;
  if (!product.id) return;

  const currencyCode = bcUtils.extractProductCurrency(product);
  const productPrice = bcUtils.extractProductPrice(product);
  const productCompareAtPrice = bcUtils.extractProductCompareAtPrice(product) || null;

  var payload = {};
  payload["product_id"] = product.id;
  payload["name"] = product.title;
  payload["currency"] = data.currency_selector.default_currency_code;
  payload["price"] = bcUtils.convertDefaultRate(productPrice, currencyCode);
  payload["compare_at_price"] = productCompareAtPrice ? bcUtils.convertDefaultRate(productCompareAtPrice, currencyCode) : null;
  payload["categories"] = product.categories;
  payload["url"] = product.url;
  payload["image_url"] = product.image_url && utils.isValidUrl(product.image_url) ? product.image_url.replace("{:size}", "600x600") : null;

  window.OnVoard('web_tracking', 'track', 'bigcommerce.product_viewed', payload);
}

//====================================
// Cart Item Added
//====================================
function generateCartItemAddedPayload() {
  let data = window.OnVoardData;
  const pageType = data.page_type;
  const shop = data.shop;
  const product = data.product;

  const eligiblePageTypes = [
    "product",
  ];

  if (!eligiblePageTypes.includes(pageType)) return;
  if (!product.id) return;

  const currencyCode = bcUtils.extractProductCurrency(product);
  const productPrice = bcUtils.extractProductPrice(product);
  const productCompareAtPrice = bcUtils.extractProductCompareAtPrice(product) || null;

  var payload = {};
  payload["product_id"] = product.id;
  payload["name"] = product.title;
  payload["currency"] = data.currency_selector.default_currency_code;
  payload["price"] = bcUtils.convertDefaultRate(productPrice, currencyCode);
  payload["compare_at_price"] = productCompareAtPrice ? bcUtils.convertDefaultRate(productCompareAtPrice, currencyCode) : null;
  payload["categories"] = product.categories;
  payload["url"] = product.url;
  payload["image_url"] = product.image_url && utils.isValidUrl(product.image_url) ? product.image_url.replace("{:size}", "600x600") : null;
  return payload
}

// Prices shown on product page is based on shop's default currency
function trackCartItemAdded(data) {
  const pageType = data.page_type;
  const shop = data.shop;
  const product = data.product;

  const eligiblePageTypes = [
    "product",
  ];

  if (!eligiblePageTypes.includes(pageType)) return;
  if (!product.id) return;

  const isAddToCart = (text) => {
    if (!text) return false;
    const validTexts = [
      "addtocart",
      "addtobag"
    ];

    // convert to lower case and only keep a-z characters
    text = text.toLowerCase().replace(/[^a-z]/gi, '');
    return validTexts.includes(text);
  };

  const triggerAddToCart = () => {
    let payload = generateCartItemAddedPayload();
    if (!payload) return;
    window.OnVoard('web_tracking', 'track', 'bigcommerce.cart_item_added', payload);
  };

  if (!window.OnVoardAddedScripts.bigCommerceTrackCartItemAdded) {
    window.OnVoardAddedScripts.bigCommerceTrackCartItemAdded = true;
    document.addEventListener('click', (evt) => {
      var target = evt.target;
      if (!target) return;

      var innerText = target.innerText;
      if (innerText && isAddToCart(innerText)) {
        triggerAddToCart();
        return;
      }

      var elId = target.getAttribute("id");
      if (elId && isAddToCart(elId)) {
        triggerAddToCart();
        return;
      }

      var elValue = target.getAttribute("value");
      if (elValue && isAddToCart(elValue)) {
        triggerAddToCart();
        return;
      }

      var elClassList = [...target.classList];
      if (elClassList.some((elClassName) => isAddToCart(elClassName))) {
        triggerAddToCart();
        return;
      }
    });
  }
}


function getHandler() {
  const handler = {};
  handler.identify = function(data) {
    identify(data);
  };

  handler.trackCheckoutStarted = function(data) {
    trackCheckoutStarted(data)
  };

  handler.trackProductViewed = function(data) {
    trackProductViewed(data);
  };

  handler.trackCartItemAdded = function(data) {
    trackCartItemAdded(data);
  };

  return handler;
}


function process(args) {
  const handler = getHandler();
  var params = [].slice.call(args);
  var ns = params.shift();
  var method = params.shift();

  // web_tracking_bigcommerce namespace
  if (ns === "web_tracking_bigcommerce") {
    if (handler[method]) {
     handler[method].apply(handler, params);
    } else {
      console.log(`web_tracking_bigcommerce does not have a ${method} method`);
    }
  }
};



export function processTracking() {
  // process queued function calls
  const queue = window.OnVoardQueue.webTrackingBigCommerce || [];
  for (var i in queue) {
    process(queue[i]);
  }

  // setup api endpoint after everything is ready
  window.OnVoardApi.webTrackingBigCommerce = function(args) {
    process(args);
  };
}



